import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { authService, getPostAuthRoute, setPendingVerificationContext, setTokens, setUserProfile } from '../../services/auth.service';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '174567635393-3fs7h8pgvpfsf1ha346m694rv9qvucm5.apps.googleusercontent.com';

const GoogleAuthButton = ({ customerType, navigate, onError, className = '' }) => {
  const buttonRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const initializeGoogle = () => {
      if (cancelled || !buttonRef.current || !window.google?.accounts?.id || !GOOGLE_CLIENT_ID) {
        return;
      }

      buttonRef.current.innerHTML = '';

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async ({ credential }) => {
          if (!credential) {
            onError?.('Google sign-in did not return a credential. Please try again.');
            return;
          }

          setIsSubmitting(true);
          onError?.('');

          try {
            const response = await authService.googleLogin(credential, customerType);
            if (response.action === 2 && response.requiresTwoFactor && response.customerId) {
              setPendingVerificationContext({
                customerId: response.customerId,
                role: response.customerType || customerType || 'RENTER',
                purpose: 'login_2fa'
              });
              navigate('/verify', {
                replace: true,
                state: {
                  customerId: response.customerId,
                  role: response.customerType || customerType || 'RENTER',
                  purpose: 'login_2fa'
                }
              });
            } else {
              setTokens(response.token, response.refreshToken);
              setUserProfile(response);
              navigate(getPostAuthRoute(response), { replace: true });
            }
          } catch (error) {
            onError?.(error.message || 'Google sign-in failed. Please try again.');
          } finally {
            setIsSubmitting(false);
          }
        },
      });

      window.google.accounts.id.renderButton(buttonRef.current, {
        theme: 'outline',
        size: 'large',
        shape: 'rectangular',
        text: 'continue_with',
        width: buttonRef.current.offsetWidth || 320,
      });

      setIsReady(true);
    };

    initializeGoogle();

    const retryId = window.setTimeout(initializeGoogle, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(retryId);
    };
  }, [customerType, navigate, onError]);

  if (!GOOGLE_CLIENT_ID) {
    return (
      <button
        type="button"
        onClick={() => onError?.('Google sign-in is not configured yet. Add REACT_APP_GOOGLE_CLIENT_ID to the client environment.')}
        className={className}
      >
        Continue with Google
      </button>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {isSubmitting && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/75">
          <Loader2 className="h-5 w-5 animate-spin text-brand-black" />
        </div>
      )}
      <div ref={buttonRef} className={isReady ? '' : 'min-h-[44px]'} />
    </div>
  );
};

export default GoogleAuthButton;
